import React from 'react';

import { useGetCustomerRiskFactors } from '../../hooks/useGetCustomerRiskFactors';
import { toUniversalUtcDate } from '../../../utils/date.utils';
import { CowbellFactorsRangePanel } from './CowbellFactorsRangePanel';
import { toFactorRangeData } from './toFactorRangeData';
import { useCowbellFactorTrends } from './useCowbellFactorTrends';

interface CowbellFactorsRangePanelContainerProps {
  readonly accountId: string;
  readonly companyName?: string;
  /**
   * The dashboard's date window (unix seconds), used for trend deltas. Arrives
   * from the URL query string, so it is a string in practice.
   */
  readonly before?: number | string;
  readonly after?: number | string;
}

/**
 * Data for the shared-axis Cowbell Factors panel: the account's risk scores
 * plus the trend series behind each factor's movement.
 */
export function CowbellFactorsRangePanelContainer({
  accountId,
  companyName,
  before,
  after,
}: Readonly<CowbellFactorsRangePanelContainerProps>) {
  const { cowbellFactorsRiskData, query } = useGetCustomerRiskFactors({
    accountId,
  });
  const { trends, trendWindowLabel } = useCowbellFactorTrends({
    accountId,
    before,
    after,
  });

  const { aggregate, factors } = React.useMemo(
    () => toFactorRangeData(cowbellFactorsRiskData, trends),
    [cowbellFactorsRiskData, trends]
  );

  const timestamp = query.data?.timestamp;

  return (
    <CowbellFactorsRangePanel
      factors={factors}
      aggregate={aggregate}
      companyName={companyName}
      updatedAt={timestamp ? toUniversalUtcDate(timestamp) : undefined}
      trendWindowLabel={trendWindowLabel}
      isLoading={query.isInitialLoading}
    />
  );
}

export default CowbellFactorsRangePanelContainer;
